import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Snackbar, Alert, AlertColor, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { planPromptJustOpened } from '../util/plan-required';

interface ToastOptions {
    severity?: AlertColor;
    duration?: number;
    persistent?: boolean;
    action?: React.ReactNode;
}

interface ToastState {
    open: boolean;
    message: string;
    severity: AlertColor;
    duration: number | null;
    action: React.ReactNode;
    id: string;
}

type ToastContextType = {
    showToast: (message: string, options?: ToastOptions) => void;
    showSuccess: (message: string, options?: Omit<ToastOptions, 'severity'>) => void;
    showError: (message: string, options?: Omit<ToastOptions, 'severity'>) => void;
    showWarning: (message: string, options?: Omit<ToastOptions, 'severity'>) => void;
    showInfo: (message: string, options?: Omit<ToastOptions, 'severity'>) => void;
    hideToast: () => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast must be used within a ToastProvider');
    return ctx;
};

// Legacy hook for backward compatibility
export const useSnackbar = () => {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useSnackbar must be used within a ToastProvider');
    return {
        showSnackbar: (message: string, severity: AlertColor = 'info') => ctx.showToast(message, { severity })
    };
};

type ToastProviderProps = {
    children: React.ReactNode;
};

const NotificationProvider: React.FC<ToastProviderProps> = ({ children }) => {
    const [toasts, setToasts] = useState<ToastState[]>([]);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const hideToast = useCallback((id?: string) => {
        setToasts(prev => {
            if (id) {
                return prev.filter(toast => toast.id !== id);
            }
            // If no id provided, close the first (oldest) toast
            return prev.slice(1);
        });
    }, []);

    const showToast = useCallback((message: string, options: ToastOptions = {}) => {
        const {
            severity = 'info',
            duration = 4000,
            persistent = false,
            action: customAction
        } = options;

        // The upgrade prompt just opened for this failure - it says it all.
        if ((severity === 'error' || severity === 'warning') && planPromptJustOpened()) return;

        const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);

        const newToast: ToastState = {
            id,
            open: true,
            message,
            severity,
            duration: persistent ? null : duration,
            action: customAction || null
        };

        setToasts(prev => {
            // The same message already on screen isn't shown again - several
            // requests failing the same way at once used to stack identical toasts.
            if (prev.some(t => t.message === message && t.severity === severity)) return prev;
            // Limit to 3 toasts max to prevent overflow
            const updatedToasts = [...prev, newToast];
            return updatedToasts.slice(-3);
        });

        // Auto-hide after duration if not persistent
        if (!persistent && duration) {
            setTimeout(() => hideToast(id), duration);
        }
    }, [hideToast]);

    const showSuccess = useCallback((message: string, options: Omit<ToastOptions, 'severity'> = {}) => {
        showToast(message, { ...options, severity: 'success' });
    }, [showToast]);

    const showError = useCallback((message: string, options: Omit<ToastOptions, 'severity'> = {}) => {
        showToast(message, { ...options, severity: 'error', duration: options.duration || 6000 });
    }, [showToast]);

    const showWarning = useCallback((message: string, options: Omit<ToastOptions, 'severity'> = {}) => {
        showToast(message, { ...options, severity: 'warning' });
    }, [showToast]);

    const showInfo = useCallback((message: string, options: Omit<ToastOptions, 'severity'> = {}) => {
        showToast(message, { ...options, severity: 'info' });
    }, [showToast]);

    const handleClose = useCallback((id: string) => (_?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        hideToast(id);
    }, [hideToast]);

    // No-arg wrapper exposed on the context - needs its own useCallback (not
    // an inline arrow rebuilt every render) so consumers that put it in a
    // dependency array (e.g. an effect that shows a toast then dismisses it
    // later) get a stable reference instead of re-running on every render.
    const hideFirstToast = useCallback(() => hideToast(), [hideToast]);

    const contextValue = useRef({
        showToast,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        hideToast: hideFirstToast
    });

    // Update ref without causing re-renders
    contextValue.current = {
        showToast,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        hideToast: hideFirstToast
    };

    return (
        <ToastContext.Provider value={contextValue.current}>
            {children}
            {toasts.map((toast, index) => (
                <Snackbar
                    key={toast.id}
                    open={toast.open}
                    onClose={handleClose(toast.id)}
                    anchorOrigin={{ 
                        vertical: 'top', 
                        horizontal: 'right' 
                    }}
                    sx={{
                        top: 24 + (index * 70), // Stack toasts vertically
                        zIndex: 1400 + index
                    }}
                >
                    <Alert
                        onClose={handleClose(toast.id)}
                        severity={toast.severity}
                        variant="filled"
                        sx={{
                            width: '100%',
                            minWidth: 300,
                            boxShadow: 3
                        }}
                        action={toast.action || (
                            <IconButton
                                size="small"
                                color="inherit"
                                onClick={handleClose(toast.id)}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        )}
                    >
                        {toast.message}
                    </Alert>
                </Snackbar>
            ))}
        </ToastContext.Provider>
    );
};

export default NotificationProvider;